import { useEffect, useState, lazy, Suspense } from"react";
import { Toaster } from"@/components/ui/toaster";
import { Toaster as Sonner } from"@/components/ui/sonner";
import { TooltipProvider } from"@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from"@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from"react-router-dom";
import { HelmetProvider } from"react-helmet-async";
import { supabase } from"@/integrations/supabase/client";
import Index from"./pages/Index";

// Retry dynamic imports once, then hard-reload to bust stale chunk references
// (fixes"Failed to fetch dynamically imported module" after a new deploy).
const PricingRedirect = () => { window.location.replace("/#pricing"); return null; };
const lazyWithRetry = <T extends React.ComponentType<any>>(
 factory: () => Promise<{ default: T }>
) =>
 lazy(async () => {
 const reloadKey = `lovable:chunk-reload:${factory.toString().slice(0, 80)}`;
 try {
 return await factory();
 } catch (err) {
 const alreadyReloaded = sessionStorage.getItem(reloadKey);
 if (!alreadyReloaded) {
 sessionStorage.setItem(reloadKey,"1");
 window.location.reload();
 return new Promise<{ default: T }>(() => {});
 }
 throw err;
 }
 });

const Auth = lazyWithRetry(() => import("./pages/Auth"));
const OAuthConsent = lazyWithRetry(() => import("./pages/OAuthConsent"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));

const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const PitchIntelligence = lazyWithRetry(() => import("./pages/PitchIntelligence"));
const PitchHistory = lazyWithRetry(() => import("./pages/PitchHistory"));
const SamplesIndex = lazyWithRetry(() => import("./pages/SamplesIndex"));
const SampleReportView = lazyWithRetry(() => import("./pages/SampleReportView"));
const TermsPage = lazyWithRetry(() => import("./pages/legal/TermsPage"));
const PrivacyPage = lazyWithRetry(() => import("./pages/legal/PrivacyPage"));
const CookiePage = lazyWithRetry(() => import("./pages/legal/CookiePage"));
const AcceptableUsePage = lazyWithRetry(() => import("./pages/legal/AcceptableUsePage"));
const TechnicalDDChecklist = lazyWithRetry(() => import("./pages/resources/TechnicalDDChecklist"));
const IPRiskGuide = lazyWithRetry(() => import("./pages/resources/IPRiskGuide"));
const SupplyChainGuide = lazyWithRetry(() => import("./pages/resources/SupplyChainGuide"));
const VsConsultants = lazyWithRetry(() => import("./pages/resources/VsConsultants"));
const AlphaSenseVsTegus = lazyWithRetry(() => import("./pages/resources/AlphaSenseVsTegus"));
const CompanyValuationMethods = lazyWithRetry(() => import("./pages/resources/CompanyValuationMethods"));
const ResourcesIndex = lazyWithRetry(() => import("./pages/resources/ResourcesIndex"));
const FinancialDDGuide = lazyWithRetry(() => import("./pages/resources/FinancialDDGuide"));
const TechnicalDDGuide = lazyWithRetry(() => import("./pages/resources/TechnicalDDGuide"));
const CommercialDDGuide = lazyWithRetry(() => import("./pages/resources/CommercialDDGuide"));
const DDChecklistTemplate = lazyWithRetry(() => import("./pages/resources/DDChecklistTemplate"));
const DDSoftwareComparison = lazyWithRetry(() => import("./pages/resources/DDSoftwareComparison"));
const QualityOfEarnings = lazyWithRetry(() => import("./pages/resources/QualityOfEarnings"));
const FreedomToOperate = lazyWithRetry(() => import("./pages/resources/FreedomToOperate"));


const queryClient = new QueryClient();

const AuthRedirectHandler = ({ children }: { children: React.ReactNode }) => {
 const navigate = useNavigate();
 const location = useLocation();

 useEffect(() => {
 const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
 // Only redirect on explicit sign-in events, NOT on INITIAL_SESSION or TOKEN_REFRESHED
 // This prevents redirect loops when the local session is stale
 if (event ==="SIGNED_IN" && session) {
 if (location.pathname ==="/" || location.pathname ==="/auth") {
 navigate("/dashboard", { replace: true });
 }
 }
 });

 return () => subscription.unsubscribe();
 }, [navigate, location.pathname]);

 return <>{children}</>;
};

const App = () => (
 <HelmetProvider>
 <QueryClientProvider client={queryClient}>
 <TooltipProvider>
 <Toaster />
 <Sonner />
 <BrowserRouter>
 <AuthRedirectHandler>
 <Suspense fallback={null}>
 <Routes>
 <Route path="/" element={<Index />} />
 <Route path="/auth" element={<Auth />} />
 <Route path="/dashboard" element={<Dashboard />} />
 <Route path="/preview/research-scope" element={<Dashboard />} />
 <Route path="/pitch-intelligence" element={<PitchIntelligence />} />
 <Route path="/pitch-intelligence/history" element={<PitchHistory />} />
 <Route path="/pricing" element={<PricingRedirect />} />
 <Route path="/samples" element={<SamplesIndex />} />
 <Route path="/samples/:slug" element={<SampleReportView />} />

 <Route path="/legal/terms" element={<TermsPage />} />
 <Route path="/legal/privacy" element={<PrivacyPage />} />
 <Route path="/legal/cookies" element={<CookiePage />} />
      <Route path="/legal/acceptable-use" element={<AcceptableUsePage />} />
 <Route path="/resources" element={<ResourcesIndex />} />
 <Route path="/resources/financial-due-diligence" element={<FinancialDDGuide />} />
 <Route path="/resources/technical-due-diligence" element={<TechnicalDDGuide />} />
 <Route path="/resources/commercial-due-diligence" element={<CommercialDDGuide />} />
 <Route path="/resources/due-diligence-checklist-template" element={<DDChecklistTemplate />} />
 <Route path="/resources/due-diligence-software-comparison" element={<DDSoftwareComparison />} />
 <Route path="/resources/quality-of-earnings-analysis" element={<QualityOfEarnings />} />
 <Route path="/resources/freedom-to-operate-assessment" element={<FreedomToOperate />} />
 <Route path="/resources/technical-due-diligence-checklist" element={<TechnicalDDChecklist />} />
 <Route path="/resources/ip-risk-analysis-guide" element={<IPRiskGuide />} />
 <Route path="/resources/supply-chain-chokepoint-analysis" element={<SupplyChainGuide />} />
 <Route path="/resources/ai-due-diligence-vs-consultants" element={<VsConsultants />} />
 <Route path="/resources/alphasense-vs-tegus-vs-point-science-analytics" element={<AlphaSenseVsTegus />} />
 <Route path="/resources/company-valuation-methods" element={<CompanyValuationMethods />} />
 <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
 <Route path="*" element={<NotFound />} />
 </Routes>
 
 </Suspense>
 </AuthRedirectHandler>
 </BrowserRouter>
 </TooltipProvider>
 </QueryClientProvider>
 </HelmetProvider>
);

export default App;
